import React, { useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import styles from './AttachmentDropdown.module.scss';
import ToggleButton from '../../ToggleButton/ToggleButton';
import UploadCard, { UploadCardRef } from '../../../modules/Company/UploadJourney/components/UploadCard/UploadCard';
import { FILE_FORMATS } from '../../../modules/Company/UploadJourney/components/UploadCard/utils/utils';
import { useOutsideClick } from '../../../utils/constants/hooks';
import { addCircleIcon, attachIcon } from '../../../assets/hostedassets';
import { SystemAttachmentKey, SYSTEM_ATTACHMENT_LABELS, ATTACHMENT_DROPDOWN_UPLOAD_LABEL } from '../constants';
import type { SystemAttachmentToggles } from '../types';

interface AttachmentDropdownProps {
    visibleKeys: SystemAttachmentKey[];
    toggles: SystemAttachmentToggles;
    onToggle: (key: SystemAttachmentKey, value: boolean) => void;
    // Upload props threaded to the reused UploadCard.
    orgId: string;
    categoryId: number;
    flowId: number;
    onFileUpload: (fileMetaData: { docName: string; id: number }) => void;
    onUploadStatusChange: (isUploading: boolean) => void;
}

// Single toggle row inside the attachment dropdown.
const AttachmentToggleRow: React.FC<{
    label: string;
    checked: boolean;
    onChange: (value: boolean) => void;
}> = ({ label, checked, onChange }) => (
    <div className={styles.ToggleRow}>
        <span className={styles.ToggleLabel}>{label}</span>
        <ToggleButton checked={checked} onChange={() => onChange(!checked)} variant="success" />
    </div>
);

const AttachmentDropdown: React.FC<AttachmentDropdownProps> = ({
    visibleKeys,
    toggles,
    onToggle,
    orgId,
    categoryId,
    flowId,
    onFileUpload,
    onUploadStatusChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const uploadCardRef = useRef<UploadCardRef>(null);

    const close = useCallback(() => setIsOpen(false), []);
    useOutsideClick(containerRef, close);

    const handleUploadStatusChange = useCallback(
        (isUploading: boolean) => {
            if (isUploading) setIsOpen(false);
            onUploadStatusChange(isUploading);
        },
        [onUploadStatusChange],
    );

    return (
        <div className={styles.AttachmentDropdown} ref={containerRef}>
            <button
                type="button"
                className={clsx(styles.TriggerIcon, { [styles.TriggerIconActive]: isOpen })}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label="Attach"
                aria-expanded={isOpen}
            >
                <img src={attachIcon} alt="Attach" />
            </button>

            {isOpen && (
                <div className={styles.DropdownPanel}>
                    {visibleKeys.map((key) => (
                        <AttachmentToggleRow
                            key={key}
                            label={SYSTEM_ATTACHMENT_LABELS[key]}
                            checked={toggles[key]}
                            onChange={(value) => onToggle(key, value)}
                        />
                    ))}

                    <button type="button" className={styles.UploadRowButton} onClick={() => uploadCardRef.current?.triggerFileSelect()}>
                        <img src={addCircleIcon} alt="" />
                        <span>{ATTACHMENT_DROPDOWN_UPLOAD_LABEL}</span>
                    </button>
                </div>
            )}

            {/* UploadCard stays mounted regardless of `isOpen`. The panel closes on
                upload START (handleUploadStatusChange → setIsOpen(false)); if the
                card lived inside the panel it would unmount mid-upload and the file
                would never finish. Kept invisible (hideUpload/isNotVisible/
                showAttachedFiles=false) — only its hidden file input is used, driven
                by the persistent uploadCardRef. acceptedFormats must be passed
                explicitly: UploadCard defaults the prop to FILE_FORMATS.EXCEL_PDF
                when it's `undefined`, so leaving it out silently drops .doc/.docx
                (and anything else) from the picker. List the document types we
                allow, including Word with its MIME types (see FILE_FORMATS.WORD). */}
            <UploadCard
                ref={uploadCardRef}
                hideUpload
                isNotVisible
                showAttachedFiles={false}
                assesseeOrgId={orgId}
                categoryId={categoryId}
                flowId={flowId}
                allowMultiple
                acceptedFormats={[...FILE_FORMATS.EXCEL_PDF, ...FILE_FORMATS.WORD]}
                setFilesMetaData={onFileUpload}
                onUploadStatusChange={handleUploadStatusChange}
                existingFiles={[] as []}
                appendTimestamp
            />
        </div>
    );
};

export default AttachmentDropdown;
